import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/20/solid';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function OptionGroupCard({
  id,
  optionGroup,
  selected,
  setSelected,
  onClickOpenOptionGroupUpsertModal,
  onClickDuplicateGroup,
  sortableId,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      key={id}
      onClick={() => {
        setSelected((prev) => ({
          ...prev,
          optiongroupid: optionGroup.id,
        }));
      }}
      onDoubleClick={() => onClickOpenOptionGroupUpsertModal(optionGroup)}
      className={classNames(
        optionGroup.id === selected.optiongroupid
          ? 'border-blue-400 bg-blue-50/70 text-slate-900 ring-1 ring-blue-200 dark:border-sky-400 dark:bg-slate-700 dark:text-slate-100 dark:ring-sky-500/30'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-300 dark:hover:bg-slate-600',
        isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
        'group mb-2 flex min-h-[5rem] items-center gap-4 rounded-md border px-4 py-2 transition-all duration-150',
      )}
    >
      <div className="min-w-0 flex-1">
        <div
          key={optionGroup.name}
          className="flex items-center py-1 text-sm font-medium"
          aria-current={
            optionGroup.id === selected.optiongroupid ? 'page' : undefined
          }
        >
          <div className="min-w-0 overflow-hidden">
            <span className="line-clamp-1 text-[0.95rem] font-semibold">
              {optionGroup.name && optionGroup.name.length > 35
                ? optionGroup.name.substring(0, 35) + '...'
                : optionGroup.name}
            </span>
            <span className="mt-0.5 block line-clamp-1 text-xs font-light text-slate-500 dark:text-slate-300">
              {optionGroup.description}
            </span>
            <p
              className={classNames(
                optionGroup.id === selected.optiongroupid
                  ? 'text-slate-500 dark:text-slate-300'
                  : 'text-slate-400',
                'mt-0.5 text-xs font-medium whitespace-normal max-h-[2.5em]',
              )}
            >
              Min: {optionGroup.min} - Max: {optionGroup.max}
            </p>
          </div>
        </div>
      </div>

      <div
        className="flex items-center"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Menu as="div" className="relative">
          <Menu.Button className="inline-flex items-center rounded-md p-1 text-slate-300 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200">
            <EllipsisVerticalIcon className="h-5 w-5" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-50 mt-1 w-36 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-slate-700 dark:ring-slate-600">
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? 'bg-slate-50 dark:bg-slate-600' : '',
                      'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                    )}
                    onClick={() =>
                      onClickOpenOptionGroupUpsertModal(optionGroup)
                    }
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Edit
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? 'bg-slate-50 dark:bg-slate-600' : '',
                      'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                    )}
                    onClick={() => onClickDuplicateGroup(optionGroup)}
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    Duplicate
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  );
}

export default OptionGroupCard;
